import React, { useEffect, useRef, useState } from "react";
import { FileText } from "@phosphor-icons/react";
import Brand from "../../components/Brand/Brand";
import AdvisorVideo, { AdvisorAvatar } from "./AdvisorMedia";
import { CONTENT, useContent } from "./content";
import { useHomeLang } from "./homeLang";
import { clientsLabel } from "./presence";
import "./needScenes.css";

// What AVRAH does about each thought in the advisor's day (same order as
// NEEDS): one object each, the moment it changes playing once. Every scene is
// laid out twice: on a 560 × 420 board beside the day, and on a tall board 340
// units across under each thought on narrow screens, where a wide board would
// be too small to read. `box` places a piece on both, as [x, y, width,
// height?] in board units, and the stylesheet turns one unit into a share of
// the scene's width, so a scene scales like a picture. Each scene has an
// advisor of its own; two are named, Cristina Pacini at 10:20 and Daniele
// Grimaldi at 19:30.

const SIDES = ["x", "y", "w", "h"];
const box = (wide, tall) => {
  if (!tall) throw new Error("NeedScenes: every piece needs its place on the tall board too");
  return Object.fromEntries([...wide.map((n, k) => [`--${SIDES[k]}`, n]), ...tall.map((n, k) => [`--t${SIDES[k]}`, n])]);
};
// `--d` is how many seconds into the scene a piece makes its entrance.
const at = (d, style) => ({ "--d": d, ...style });

// "Inviato a 500 clienti": who a video went to, as the scenes show it.
const SENT_TO = {
  it: (clients) => `Inviato a ${clientsLabel(clients)}`,
  en: (clients) => `Sent to ${clients.toLocaleString("en-US")} ${clients === 1 ? "client" : "clients"}`,
};

// A number that counts up from zero as the scene plays (see .ns-count): a
// figure for each of its places, each in its slot (.fig), the figures before
// the first shown only from their place up (`--from`).
const Count = ({ to, d, dur, className = "" }) => {
  const places = String(to).length;
  return (
    <span className={`ns-count ${className}`} style={{ "--to": to, "--d": d, "--dur": dur }}>
      {Array.from({ length: places }, (_, k) => {
        const place = 10 ** (places - 1 - k);
        return <span key={place} className="fig ns-fig" style={{ "--place": place, "--from": place > 1 ? place : 0 }} />;
      })}
    </span>
  );
};

/* 07:45: "Come posso essere presente per tutti?" */

// The share of clients shown as having watched the update. Illustrative.
const SEEN_SHARE = 0.9;
// When the views start coming in, and how long until the last, in seconds.
const VIEWS = { d: 0.9, dur: 2.4 };
// The first few to watch, as the faces a messaging app puts under a message.
const WATCHERS = ["AA", "GD", "MB", "EM", "CD", "FD", "IB"];
// The count goes between `seenBefore` and `seenAfter`.
const UPDATE_WORDS = {
  it: { title: "Il punto di ottobre sul portafoglio", seenBefore: "Visto da ", seenAfter: " clienti" },
  en: { title: "Your October portfolio update", seenBefore: "Watched by ", seenAfter: " clients" },
};

function ClientUpdate() {
  const lang = useHomeLang();
  const words = UPDATE_WORDS[lang];
  const { clients } = CONTENT.it.NEEDS[0];
  const seen = Math.round(clients * SEEN_SHARE);
  return (
    <>
      <div className="ns-frame ns-hero-frame ns-in" style={box([88, 40, 384], [20, 20, 300])}><AdvisorVideo time="07:45" /></div>
      <p className="ns-caption ns-in" style={at(0.2, box([40, 274, 480], [20, 206, 300]))}>
        <strong>{words.title}</strong>
        <span>{SENT_TO[lang](clients)}</span>
      </p>
      <div className="ns-viewers" style={box([40, 338, 480], [20, 290, 300])}>
        <span className="ns-pile">
          {WATCHERS.map((initials, i) => (
            <span key={initials} className="ns-ava ns-in" style={at(VIEWS.d + i * 0.18)}>{initials}</span>
          ))}
        </span>
        <span className="ns-in" style={at(VIEWS.d)}>{words.seenBefore}<Count to={seen} d={VIEWS.d} dur={VIEWS.dur} className="ns-seen" />{words.seenAfter}</span>
      </div>
    </>
  );
}

/* 10:20: "Come mi distinguo?" */

// Cristina's phone: her first message to Ferdinando, a client Alba sent her way, is
// a video of herself, as wide as the chat so her name on it can be read, and
// he answers it with a date.
const CHAT = {
  it: ["Piacere Ferdinando, sono Cristina. Alba mi ha parlato di lei.", "Grazie del video. Giovedì alle 10 le va bene?", "Perfetto, a giovedì."],
  en: ["Hello Ferdinando, I’m Cristina. Alba told me about you.", "Thanks for the video. Would Thursday at 10 work for you?", "Perfect, see you Thursday."],
};

function Introduction() {
  const [hello, reply, confirm] = CHAT[useHomeLang()];
  return (
    <div className="ns-phone ns-chat-screen ns-rise" style={box([130, 20, 300, 460], [20, 24, 300, 476])}>
      <span className="ns-island" />
      <span className="ns-status">21:14</span>
      <div className="ns-chat-head">
        <span className="ns-ava ns-ava-ink">FZ</span>
        <strong>Ferdinando Zanotti</strong>
      </div>
      <div className="ns-chat">
        <div className="ns-msg ns-msg-out ns-msg-video ns-in" style={at(0.6)}>
          <span className="ns-thumb"><AdvisorVideo time="10:20" /><span className="ns-dur">0:45</span></span>
          <span className="ns-msg-text">{hello}</span>
        </div>
        <div className="ns-msg ns-msg-in ns-in" style={at(1.8)}>{reply}</div>
        <div className="ns-msg ns-msg-out ns-in" style={at(2.6)}>{confirm}</div>
      </div>
    </div>
  );
}

/* 13:05: "I figli dei miei clienti sanno chi sono?" */

// The video as its advisor sees it once it has gone to the whole Dini
// family: her two clients watch it, then their children, who had never met
// her (`seen`, that many seconds in), and one of the children writes back.
const DINI = [
  { initials: "GD", name: "Girolamo Dini", tie: "client", seen: 0.9 },
  { initials: "FD", name: "Fiorella Dini", tie: "client", seen: 1.2 },
  { initials: "VD", name: "Virginia Dini", tie: "daughter", seen: 2 },
  { initials: "DD", name: "Diego Dini", tie: "son", seen: 2.4 },
];
const REPLY = 3.2;
// `ties` names each of DINI by how they are tied to the advisor, in order.
const FAMILY_WORDS = {
  it: {
    title: "Il team che ti segue",
    sent: "Inviato alla famiglia Dini",
    ties: { client: "cliente", daughter: "figlia", son: "figlio" },
    was: "Inviato",
    now: "Visto",
    reply: "Piacere di conoscerla. Quando possiamo vederci?",
  },
  en: {
    title: "The team looking after you",
    sent: "Sent to the Dini family",
    ties: { client: "client", daughter: "daughter", son: "son" },
    was: "Sent",
    now: "Seen",
    reply: "Nice to meet you. When can we sit down together?",
  },
};

function Family() {
  const words = FAMILY_WORDS[useHomeLang()];
  return (
    <>
      <div className="ns-frame ns-hero-frame ns-in" style={box([24, 70, 300], [20, 20, 300])}><AdvisorVideo time="13:05" /></div>
      <p className="ns-caption ns-caption-start ns-in" style={at(0.2, box([24, 256, 300], [20, 203, 300]))}>
        <strong>{words.title}</strong>
        <span>{words.sent}</span>
      </p>
      <ul className="ns-panel ns-family ns-in" style={at(0.3, box([344, 70, 192], [20, 266, 300]))}>
        {DINI.map((m) => (
          <li key={m.initials}>
            <span className="ns-ava">{m.initials}</span>
            <span className="ns-who"><strong>{m.name}</strong><small>{words.ties[m.tie]}</small></span>
            <span className="ns-flag" style={at(m.seen)}><span className="ns-was">{words.was}</span><span className="ns-now">{words.now}</span></span>
          </li>
        ))}
      </ul>
      <div className="ns-note ns-in" style={at(REPLY, box([344, 260, 192], [20, 448, 300]))}>
        <span className="ns-note-head"><span className="ns-ava ns-ava-ink">VD</span><strong>Virginia Dini</strong><small>18:10</small></span>
        {words.reply}
      </div>
    </>
  );
}

/* 16:40: "Come trovo il tempo per tutti?" */

// The advisor's working week, 9:00 to 18:00 in ten-minute steps.
const DAYS = {
  it: ["lun", "mar", "mer", "gio", "ven"],
  en: ["Mon", "Tue", "Wed", "Thu", "Fri"],
};
const WEEKDAYS = DAYS.it.length;
const STEPS = 54;
// Before: the same document explained client by client wherever each could be
// fitted in, day by day, as [first step, steps long, lane]. Two that clash
// share the day side by side, in lanes 1 and 2.
const ASKED = [
  [[0, 3], [4, 2], [7, 3, 1], [8, 3, 2], [13, 2], [16, 4], [21, 2], [25, 3], [29, 2, 1], [30, 3, 2], [35, 2], [39, 4], [45, 2], [49, 3]],
  [[1, 2], [4, 4], [9, 2], [12, 3, 1], [13, 3, 2], [18, 2], [22, 3], [26, 4], [31, 2], [34, 3, 1], [35, 3, 2], [40, 2], [44, 3], [48, 2], [51, 3]],
  [[0, 2], [3, 3], [7, 2], [10, 4], [15, 2, 1], [16, 2, 2], [20, 3], [24, 2], [28, 4], [33, 2], [36, 3], [41, 2, 1], [41, 3, 2], [46, 4], [51, 2]],
  [[2, 3], [6, 2], [9, 3], [13, 2, 1], [14, 3, 2], [19, 4], [24, 2], [27, 3], [32, 2], [35, 4], [41, 2], [44, 3, 1], [45, 2, 2], [49, 3]],
  [[0, 4], [5, 2], [8, 3], [12, 2], [15, 3, 1], [16, 3, 2], [21, 2], [24, 4], [30, 3], [34, 2], [38, 3, 1], [39, 3, 2], [44, 2], [47, 4], [52, 2]],
];
// Where across its day each lane's block has its middle.
const LANE_MIDDLE = [0.5, 0.3, 0.7];
// After: the video the advisor makes once, in a couple of minutes (`length`, in
// seconds), sent to every client who asked, and the meetings he then starts
// himself, the same hours each day: with whom, and what for.
const VIDEO = { day: 0, from: 0, steps: 15, length: 120 };
const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
const MEETING_STEPS = 12;
// The meetings' places in the week, and with whom, in PLANNED_WORDS when it
// is not one name in every language.
const PLANNED = [
  { day: 0, from: 15, who: "Maurizio B." },
  { day: 1, from: 15, who: "Alba A." },
  { day: 2, from: 15, who: "Carmen D." },
  { day: 3, from: 15, who: "Ilario B." },
  { day: 4, from: 15, who: "Eva M." },
  { day: 1, from: 36, who: "Ferdinando Z." },
  { day: 2, from: 36 },
  { day: 3, from: 36, who: "Serafino C." },
];
// What each meeting in PLANNED is for, in order. Every word short enough for
// a day on the tall board.
const PLANNED_WORDS = {
  it: [
    { why: "Piano pensione" },
    { why: "Bilancio annuale" },
    { why: "Piano per i figli" },
    { why: "Polizza vita" },
    { why: "Seconda casa" },
    { why: "Primo incontro" },
    { who: "Famiglia Dini", why: "Eredità" },
    { why: "Nuovi risparmi" },
  ],
  en: [
    { why: "Retirement plan" },
    { why: "Annual review" },
    { why: "Kids’ future" },
    { why: "Life insurance" },
    { why: "Second home" },
    { why: "First meeting" },
    { who: "Dini family", why: "Inheritance" },
    { why: "New savings" },
  ],
};
const VIDEO_TITLE = { it: "Il documento, in breve", en: "The document, in brief" };
// When the video comes in, in seconds. It plays through, its playhead
// running for as long as the questions take to go into it (see .ns-controls),
// then settles into its place in the week, and the meetings follow.
const SENT = 1.9;
const PLAYING = SENT + 0.25;
const SETTLED = SENT + 2.3;
const rows = (from, steps) => `${from + 2} / span ${steps}`;

function TimeSaved() {
  const lang = useHomeLang();
  const planned = PLANNED.map((plan, i) => ({ ...plan, ...PLANNED_WORDS[lang][i] }));
  const asked = ASKED.flatMap((blocks, day) => blocks.map(([from, steps, lane = 0], k) => ({ day, from, steps, lane, k })));
  return (
    <div className="ns-week" style={{ ...box([24, 22, 512, 376], [12, 20, 316, 380]), "--steps": STEPS }}>
      {DAYS[lang].map((day, k) => <span key={day} className="ns-weekday" style={{ gridColumn: k + 1 }}>{day}</span>)}
      {DAYS[lang].map((day, k) => <span key={day} className="ns-day" style={{ gridColumn: k + 1 }} />)}
      {asked.map(({ day, from, steps, lane, k }, i) => (
        <span
          key={`${day}-${from}-${lane}`}
          className={`ns-asked ns-lane-${lane}`}
          style={{
            gridColumn: day + 1,
            gridRow: rows(from, steps),
            "--day": day,
            "--across": LANE_MIDDLE[lane],
            "--down": from + steps / 2,
            "--in": 0.05 + (k * WEEKDAYS + day) * 0.012,
            "--out": SENT + 0.3 + i * 0.0105,
          }}
        >
          <FileText weight="fill" />
        </span>
      ))}
      {/* the video as it stays in the week, once the player has landed on it */}
      <span className="ns-sent" style={at(SETTLED - 0.15, { gridColumn: VIDEO.day + 1, gridRow: rows(VIDEO.from, VIDEO.steps) })}>
        <span className="ns-thumb">
          <AdvisorVideo time="16:40" />
          <span className="ns-dur">{clock(VIDEO.length)}</span>
        </span>
        {SENT_TO[lang](asked.length)}
      </span>
      {planned.map((plan, i) => (
        <span key={`${plan.day}-${plan.from}`} className="ns-plan ns-in" style={at(SETTLED + 0.2 + i * 0.12, { gridColumn: plan.day + 1, gridRow: rows(plan.from, MEETING_STEPS) })}>
          <strong>{plan.who}</strong>
          <small>{plan.why}</small>
        </span>
      ))}
      <span className="ns-player" style={at(SENT, { "--day": VIDEO.day, "--start": VIDEO.from, "--length": VIDEO.length })}>
        <span className="ns-video-title">{VIDEO_TITLE[lang]}</span>
        <span className="ns-screen"><AdvisorVideo time="16:40" /></span>
        <span className="ns-controls" style={at(SENT + 1.6, { "--play": PLAYING })}>
          <span className="ns-pause" />
          <span className="ns-track"><span className="ns-knob" /></span>
          <span className="ns-elapsed" />
        </span>
      </span>
    </div>
  );
}

/* 19:30: "Come anticipo le domande sui mercati?" */

// The scene's words; `fall` is the FTSE MIB's at the open, as each language
// writes the figure.
const MARKET_WORDS = {
  it: {
    fall: "−3,8%",
    mark: "il video di Daniele, 11:40",
    reply: "Grazie Daniele. Allora resto investito.",
    date: "martedì 20 ottobre",
    now: "ora",
    video: "Nuovo video: cosa succede oggi sui mercati",
    stocks: "Borsa",
    open: "FTSE MIB −3,8% in apertura",
    news: "Notizie",
    headline: "Vendite su tutti i listini europei",
  },
  en: {
    fall: "−3.8%",
    mark: "Daniele’s video, 11:40",
    reply: "Thanks, Daniele. I’ll stay invested, then.",
    date: "Tuesday, October 20",
    now: "now",
    video: "New video: what’s happening in the markets today",
    stocks: "Stocks",
    open: "FTSE MIB −3.8% at the open",
    news: "News",
    headline: "Selling across all European markets",
  },
};

function MarketComment() {
  const words = MARKET_WORDS[useHomeLang()];
  return (
    <>
      <div className="ns-panel ns-chart-panel ns-in" style={box([24, 28, 304, 282], [20, 20, 300, 250])}>
        <div className="ns-chart-head">
          <strong>FTSE MIB</strong>
          <span className="ns-down">{words.fall}</span>
        </div>
        {/* The labels are set in the page's type, not the chart's, so they
            keep the scene's smallest size however small the chart draws. */}
        <div className="ns-chart">
          <svg viewBox="0 0 276 150">
            <path d="M0 8 L16 12 L32 28 L50 34 L68 58 L86 70 L102 66 L120 88 L138 98 L156 94 L184 110" className="ns-price ns-draw" pathLength="1" style={at(0.3)} />
            <path d="M184 110 L200 103 L216 106 L234 97 L252 99 L276 92" className="ns-price ns-draw" pathLength="1" style={at(2.1)} />
            <line x1="184" x2="184" y1="0" y2="150" className="ns-mark ns-in" style={at(1.5)} />
            <circle cx="184" cy="110" r="4.5" className="ns-mark-dot ns-in" style={at(1.5)} />
          </svg>
          <span className="ns-mark-label ns-in" style={at(1.5)}>{words.mark}</span>
          <span className="ns-hours">{["9:00", "10:00", "11:00", "12:00", "13:00"].map((t) => <span key={t}>{t}</span>)}</span>
        </div>
      </div>
      <div className="ns-note ns-in" style={at(2.9, box([24, 326, 304], [20, 566, 300]))}>
        <span className="ns-note-head"><span className="ns-ava ns-ava-ink">MB</span><strong>Maurizio B.</strong><small>11:52</small></span>
        {words.reply}
      </div>
      <div className="ns-phone ns-lock ns-rise" style={box([348, 28, 192, 420], [44, 290, 252, 420])}>
        <span className="ns-island" />
        <span className="ns-clock">11:40<small>{words.date}</small></span>
        <div className="ns-notifs">
          <div className="ns-slot" style={at(1.5)}>
            <div className="ns-notif">
              <span className="ns-ava"><AdvisorAvatar /></span>
              <span className="ns-notif-body">
                <span className="ns-notif-top"><strong>Daniele Grimaldi</strong><small>{words.now}</small></span>
                {words.video}
                <span className="ns-thumb"><AdvisorVideo time="19:30" /><span className="ns-dur">2:30</span></span>
              </span>
            </div>
          </div>
          <div className="ns-notif ns-earlier ns-in" style={at(0.5)}>
            <span className="ns-ico ns-ico-down" />
            <span className="ns-notif-body">
              <span className="ns-notif-top"><strong>{words.stocks}</strong><small>09:05</small></span>
              {words.open}
            </span>
          </div>
          <div className="ns-notif ns-earlier ns-in" style={at(0.8)}>
            <span className="ns-ico">N</span>
            <span className="ns-notif-body">
              <span className="ns-notif-top"><strong>{words.news}</strong><small>10:12</small></span>
              {words.headline}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// Each scene with the height of its tall board, in board units.
const SCENES = [
  { Scene: ClientUpdate, tall: 376 },
  { Scene: Introduction, tall: 440 },
  { Scene: Family, tall: 552 },
  { Scene: TimeSaved, tall: 420 },
  { Scene: MarketComment, tall: 652 },
];

// A need's scene with its answer underneath, beside the day on wide screens.
// Both stay out of the accessibility tree: the day's list carries every
// thought with its answer, in order, so a screen reader is not interrupted at
// every step of scroll.
export function SceneWindow({ index }) {
  const { Scene } = SCENES[index];
  const { NEEDS } = useContent();
  return (
    <div className="nd-box">
      <div className="ns" aria-hidden="true">
        {/* Keyed so the scene plays from the start on every change. */}
        <div key={index} className="ns-stage">
          <Scene />
        </div>
      </div>
      <p className="nd-ans t-h3" aria-hidden="true"><Brand>{NEEDS[index].answer}</Brand></p>
    </div>
  );
}

// On narrow screens each thought has its own scene under it, on the tall
// board, which plays from the start each time it comes into view: it opens
// once a quarter of it shows and closes once it has gone. Hidden on wide
// screens, it never comes into view there.
export function SceneInView({ index }) {
  const { Scene, tall } = SCENES[index];
  const board = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([seen]) => {
      if (seen.intersectionRatio >= 0.25) setOpen(true);
      else if (!seen.isIntersecting) setOpen(false);
    }, { threshold: [0, 0.25] });
    observer.observe(board.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={board} className="ns ns-tall nd-scene" style={{ "--tall": tall }} aria-hidden="true">
      {open && <div className="ns-stage"><Scene /></div>}
    </div>
  );
}
